import { useEffect, useMemo, useRef, useState } from 'react';
import { RouterProvider } from 'react-router-dom';

// project imports
import router from 'routes';
import ThemeCustomization from 'themes';
import ScrollTop from 'components/ScrollTop';
import { AuthProvider } from './lib/auth';
import InAppBrowserBanner from 'components/InAppBrowserBanner';


// 🔹 PWA: service worker registration
import { registerSW } from 'virtual:pwa-register';

// MUI
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';

export default function App() {
  const [offlineReady, setOfflineReady] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);

  // Keep a stable reference to the update function returned by registerSW
  const updateFnRef = useRef(null);

  // Only register SW once
  useEffect(() => {
    // If a new SW downloaded in a previous session is waiting, activate it now.
    // This makes pull-to-refresh (or any manual reload) pick up the new version immediately.
    const hadController = !!navigator.serviceWorker?.controller;
    navigator.serviceWorker?.getRegistration().then((reg) => {
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    });

    // After skipWaiting the browser fires controllerchange — reload so the new SW serves content.
    // Guard with hadController so we don't reload on the very first SW install.
    const onControllerChange = () => {
      if (hadController) window.location.reload();
    };
    navigator.serviceWorker?.addEventListener('controllerchange', onControllerChange);

    const updateSW = registerSW({
      immediate: true,

      onOfflineReady() {
        setOfflineReady(true);
      },

      onNeedRefresh() {
        // New version downloaded — will activate on the next page refresh automatically.
        // Also show toast so users can update right now if they want.
        setUpdateReady(true);
      },

      onRegisterError(e) {
        console.error('Service worker registration error', e);
      }
    });

    updateFnRef.current = updateSW;

    // ✅ Check when app returns to foreground
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        try {
          updateFnRef.current?.(true);
        } catch (e) {
          console.warn('SW update check failed (visibilitychange)', e);
        }
      }
    };

    // ✅ Also check on focus (some mobile browsers fire this more reliably)
    const onFocus = () => {
      try {
        updateFnRef.current?.(true);
      } catch (e) {
        console.warn('SW update check failed (focus)', e);
      }
    };

    // ✅ Check again once online (helps if they were offline during deploy)
    const onOnline = () => {
      try {
        updateFnRef.current?.(true);
      } catch (e) {
        console.warn('SW update check failed (online)', e);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);

    // ✅ Optional: lightweight polling while app stays open
    // Good for "app left open in background then resumed later"
    const pollId = window.setInterval(() => {
      // Only poll when visible to reduce unnecessary work
      if (document.visibilityState === 'visible') {
        try {
          updateFnRef.current?.(true);
        } catch (e) {
          // ignore
        }
      }
    }, 60 * 1000); // every 60s (tune to 120s if you want)

    return () => {
      navigator.serviceWorker?.removeEventListener('controllerchange', onControllerChange);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      window.clearInterval(pollId);
    };
  }, []);

  const handleReloadToUpdate = async () => {
    try {
      // This tells the waiting SW to activate, then reloads the page
      await updateFnRef.current?.(true);
    } catch (e) {
      console.error('Failed to apply update', e);
      // Fallback: regular reload
      window.location.reload();
    }
  };

  const handleCloseOffline = () => setOfflineReady(false);
  const handleCloseUpdate = () => setUpdateReady(false);

  return (
    <AuthProvider>
      <ThemeCustomization>
        <InAppBrowserBanner />
        <ScrollTop>
          <RouterProvider router={router} />
        </ScrollTop>

        {/* Offline ready toast */}
        <Snackbar
          open={offlineReady}
          autoHideDuration={4000}
          onClose={handleCloseOffline}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseOffline} severity="success" variant="filled">
            App ready for offline use
          </Alert>
        </Snackbar>

        {/* Update available toast */}
        <Snackbar
          open={updateReady}
          onClose={handleCloseUpdate}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseUpdate}
            severity="info"
            variant="filled"
            action={
              <Button color="inherit" size="small" onClick={handleReloadToUpdate}>
                Reload
              </Button>
            }
          >
            New version available
          </Alert>
        </Snackbar>
      </ThemeCustomization>
    </AuthProvider>
  );
}
